import { Task } from 'app/task/task'
import * as R from 'ramda'
import { offerIsSyncing, isOfferUnsaved, isOfferFinalized } from '../../common/offer-state-predicates'
import { updateDb } from './db-versions'
import Dexie from 'dexie'
import {
  ClientOffer,
  Offer,
  OpportunityCustomer,
  CustomerSfId,
  OpportunitySfId,
  OfferPreviousVersion,
  Opportunity
} from '../../../types/types'
import { mergeObjects } from 'app/app/common/ramda-utils'

class ClientDatabase extends Dexie {
  offers!: Dexie.Table<ClientOffer, OpportunitySfId>

  accounts!: Dexie.Table<OpportunityCustomer, CustomerSfId>

  // TODO: add typings to config
  config!: Dexie.Table<Record<any, any>, string>

  constructor() {
    super('kcc', { autoOpen: true })
    this.on('versionchange', event => {
      console.info(`IndexedDB is updating. Old version: ${event.oldVersion}, new version: ${event.newVersion}`)
      updateDb(this)
    })
    updateDb(this)
    this.offers = this.table('offers')
    this.accounts = this.table('accounts')
    this.config = this.table('config')
  }
}

const db = new ClientDatabase()

const fromPromiseCatch = <T>(p: () => Dexie.Promise<T>) =>
  new Task<T>((reject, resolve) => {
    p().then(resolve).catch(reject)
  })

export const clear = () => {
  db.offers.clear()
  db.accounts.clear()
  db.config.clear()
}

export const updateConfigurationsPromise = async (list: Array<Record<any, any>>): Promise<void> => {
  await Dexie.Promise.resolve(db.config.bulkPut(list))
}

export const removeConfig = (id: string) => fromPromiseCatch(() => db.config.delete(id))
export const updateConfig = (id: string, value: Record<any, any>) =>
  fromPromiseCatch(() => db.config.put(R.assoc('id', id, value)))

const fetchConfigPromise = (name: string) =>
  db.config
    .where('id')
    .equals(name)
    .toArray()
    .then(config => R.head(config) || {})
export const fetchConfig = (name: string) => fromPromiseCatch(() => fetchConfigPromise(name))

// export const fetchConfigWithPromise = (name: string) => () => fetchConfigPromise(name)

export const fetchConfigWithPromise = async <T>(key: string) =>
  (await Dexie.Promise.resolve(fetchConfigPromise(key))) as Promise<T>

export const fetchAccountsPromise = async () => await Dexie.Promise.resolve(db.accounts.toArray())

export const removeNotOwnedOffersPromise = async (opportunityIds: OpportunitySfId[]) =>
  await Dexie.Promise.resolve(
    db.offers
      .where('opportunityId')
      .noneOf(opportunityIds)
      .delete()
      .then(count => {
        console.info(`removed ${count} offer(s) that are not active and/or not owned by the current user`)
      })
  )

export const updateAccountsPromise = async (accounts: OpportunityCustomer[]) =>
  await Dexie.Promise.resolve(db.accounts.clear().then(_ => db.accounts.bulkPut(accounts)))

export const updateAccountPromise = async (account: OpportunityCustomer, opportunity: Opportunity) =>
  await Dexie.Promise.resolve(
    db.transaction('rw', db.accounts, () =>
      db.accounts
        .where('id')
        .equals(account.id)
        .toArray()
        .then(xs => R.head(xs || []))
        .then(async (dbAccount: OpportunityCustomer | undefined) => {
          if (!dbAccount) {
            throw new Error('Account not found with id: ' + account.id)
          }
          const opportunities = dbAccount.opportunities.map(opp => {
            if (opp.id === opportunity.id) {
              return opportunity
            }
            return opp
          })
          const updatedAccount = {
            ...account,
            opportunities
          }
          await db.accounts.put(updatedAccount)
          return updatedAccount
        })
    )
  )

export const fetchAccountPromise = async (accountId: OpportunitySfId) =>
  await Dexie.Promise.resolve(db.accounts.where('id').equals(accountId).toArray()).then(
    (accounts: OpportunityCustomer[]) => R.head(accounts) || {}
  )

export const getOffers = () => fromPromiseCatch(() => db.offers.toCollection().toArray())
export const getOffersPromise = async () => await Dexie.Promise.resolve(db.offers.toCollection().toArray())

export const getOfferByOpportunityIdPromise = async (opportunityId: OpportunitySfId) => {
  if (R.isNil(opportunityId)) {
    return new Dexie.Promise<ClientOffer>((_, reject) => {
      reject('missing opportunityId parameter')
    })
  }
  return R.head((await db.offers.where('opportunityId').equals(opportunityId).toArray()) || [])
}
const checkForNewerFinalizationProgress = (
  finalizationProgress: Record<string, string> | undefined,
  newProgress: Record<string, string>,
  finalized: boolean
) =>
  !finalized &&
  (!(finalizationProgress && finalizationProgress.percentComplete) ||
    parseInt(finalizationProgress.percentComplete, 10) < parseInt(newProgress.percentComplete, 10))

const shouldUpdateOffer = (dbOffer: Partial<ClientOffer>, updatedOffer: ClientOffer) => {
  const { finalizationProgress, finalized } = dbOffer
  const { finalizationProgress: newProgress, finalized: hasFinalized } = updatedOffer

  return (
    (hasFinalized && !finalized) ||
    !newProgress ||
    !Object.keys(newProgress).length ||
    checkForNewerFinalizationProgress(finalizationProgress, newProgress, !!finalized)
  )
}

const getUpdatedOfferData = (dbOffer: ClientOffer | undefined, updatedOffer: ClientOffer, forceUpdate = false) =>
  forceUpdate || shouldUpdateOffer(dbOffer || { finalized: false, finalizationProgress: {} }, updatedOffer)
    ? db.offers.put(updatedOffer).then(_ => updatedOffer)
    : dbOffer

export const updateOfferPromise = async (offer: Offer, forceUpdate = false) => {
  const { opportunityId } = offer
  return await Dexie.Promise.resolve(
    db.transaction('rw', db.offers, () =>
      db.offers
        .where('opportunityId')
        .equals(opportunityId)
        .toArray()
        .then(xs => R.head(xs || []))
        .then(dbOffer =>
          getUpdatedOfferData(
            dbOffer,
            mergeObjects(
              { ...offer, clientId: opportunityId },
              dbOffer != null ? { lastSyncStarted: dbOffer.lastSyncStarted } : {}
            ),
            forceUpdate
          )
        )
    )
  )
}

export const getUnsavedOffersPromise = async () =>
  await Dexie.Promise.resolve(
    db.offers
      .toCollection()
      .and(isOfferUnsaved)
      .and(R.complement(isOfferFinalized))
      .and(R.complement(offerIsSyncing))
      .toArray()
  )

export const updateOfferDetails = (
  opportunityId: OpportunitySfId,
  details: Partial<ClientOffer>,
  forceUpdate = false
) =>
  getOfferByOpportunityIdPromise(opportunityId).then(offer => {
    if (offer === undefined) {
      throw new Error('Offer not found with opportunityId: ' + opportunityId)
    }
    const previousVersionIndex = R.findIndex(({ id }) => id === offer.id, offer.previousVersions)
    const previousVersionLens = R.lensPath<ClientOffer, OfferPreviousVersion[]>([
      'previousVersions',
      previousVersionIndex
    ])

    const updatedOffer = R.pipe<ClientOffer, ClientOffer, ClientOffer>(
      o => mergeObjects(o, details),
      o => (previousVersionIndex === -1 ? o : R.over(previousVersionLens, version => mergeObjects(version, details), o))
    )(offer)

    return updateOfferPromise(updatedOffer, forceUpdate)
  })

/* Updates offer with given function */
export const updateOfferWith = (opportunityId: OpportunitySfId, f: (offer: ClientOffer) => ClientOffer) =>
  getOfferByOpportunityIdPromise(opportunityId).then(offer => updateOfferPromise(f(offer!)))

export const updateOfferSAPDetails = (opportunityId: string) =>
  getOfferByOpportunityIdPromise(opportunityId).then(offer => {
    if (offer === undefined) {
      throw new Error('Offer not found with opportunityId: ' + opportunityId)
    }
    return updateOfferPromise({ ...offer, sapOIStartTimestamp: null }, true)
  })
